import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Bed, Bath, Maximize, ChevronLeft, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryData, setInquiryData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    message: ''
  });

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${propertyId}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
        toast.error('Property not found');
        navigate('/properties');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, navigate]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inquiries`, {
        ...inquiryData,
        property_id: propertyId
      });
      toast.success('Inquiry sent successfully!');
      setShowInquiryForm(false);
      setInquiryData({ user_name: '', user_email: '', user_phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20" data-testid="loading-property">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const propertyImages = property.images || [];
  const displayImages = propertyImages.length > 0 ? propertyImages : ['https://images.unsplash.com/photo-1570709073200-5c34c5fa8688'];
  const currentImage = displayImages[selectedImage];
  const cityName = property.location ? property.location.city : '';
  const districtName = property.location ? property.location.district : '';
  const propertyPrice = property.price || 0;
  const amenitiesList = property.amenities || [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/properties')}
          className="mb-6 flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Properties
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="relative h-96 md:h-[500px] rounded-lg overflow-hidden" data-testid="property-main-image">
              <img
                src={currentImage}
                alt={property.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md">
                  {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                </span>
              </div>
            </div>

            {displayImages.length > 1 && (
              <div className="flex gap-4 overflow-x-auto" data-testid="property-thumbnails">
                {displayImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`View ${idx + 1}`}
                    className={`w-24 h-24 rounded-lg object-cover cursor-pointer border-2 transition-all ${
                      idx === selectedImage ? 'border-primary' : 'border-transparent hover:border-slate-300'
                    }`}
                    onClick={() => setSelectedImage(idx)}
                  />
                ))}
              </div>
            )}

            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100" data-testid="property-details">
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">{property.title}</h1>
              
              <div className="flex items-center text-slate-600 mb-6">
                <MapPin className="w-5 h-5 mr-2" />
                <span className="text-lg">
                  {cityName && `${cityName}, `}
                  {districtName}, Kerala
                </span>
              </div>

              <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-200">
                {property.bedrooms > 0 && (
                  <div className="flex items-center gap-2" data-testid="property-bedrooms">
                    <Bed className="w-5 h-5 text-primary" />
                    <span className="font-medium">{property.bedrooms} Bedrooms</span>
                  </div>
                )}
                {property.bathrooms > 0 && (
                  <div className="flex items-center gap-2" data-testid="property-bathrooms">
                    <Bath className="w-5 h-5 text-primary" />
                    <span className="font-medium">{property.bathrooms} Bathrooms</span>
                  </div>
                )}
                {property.area > 0 && (
                  <div className="flex items-center gap-2" data-testid="property-area">
                    <Maximize className="w-5 h-5 text-primary" />
                    <span className="font-medium">{property.area} sq.ft</span>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-heading font-semibold mb-3">Description</h2>
                <p className="text-slate-600 leading-relaxed">{property.description}</p>
              </div>

              {amenitiesList.length > 0 && (
                <div data-testid="property-amenities">
                  <h2 className="text-2xl font-heading font-semibold mb-3">Amenities</h2>
                  <div className="flex flex-wrap gap-2">
                    {amenitiesList.map((amenity, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-slate-100 text-slate-700 rounded-md text-sm"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 sticky top-24" data-testid="inquiry-section">
              <div className="mb-6 pb-6 border-b border-slate-200">
                <span className="text-sm text-slate-600 mb-2 block">Price</span>
                <span className="text-4xl font-bold text-primary">
                  ₹{propertyPrice.toLocaleString('en-IN')}
                </span>
                {property.listing_type === 'rent' && (
                  <span className="text-slate-600">/month</span>
                )}
              </div>

              {!showInquiryForm ? (
                <Button
                  data-testid="inquiry-button"
                  onClick={() => setShowInquiryForm(true)}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-medium"
                >
                  Send Inquiry
                </Button>
              ) : (
                <form onSubmit={handleInquiry} className="space-y-4" data-testid="inquiry-form">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <Input
                      data-testid="inquiry-name"
                      type="text"
                      required
                      value={inquiryData.user_name}
                      onChange={(e) => setInquiryData({ ...inquiryData, user_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      data-testid="inquiry-email"
                      type="email"
                      required
                      value={inquiryData.user_email}
                      onChange={(e) => setInquiryData({ ...inquiryData, user_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      data-testid="inquiry-phone"
                      type="tel"
                      value={inquiryData.user_phone}
                      onChange={(e) => setInquiryData({ ...inquiryData, user_phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <Textarea
                      data-testid="inquiry-message"
                      rows={4}
                      required
                      value={inquiryData.message}
                      onChange={(e) => setInquiryData({ ...inquiryData, message: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="inquiry-submit"
                      type="submit"
                      className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </Button>
                    <Button
                      data-testid="inquiry-cancel"
                      type="button"
                      variant="outline"
                      onClick={() => setShowInquiryForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200">
                <Button
                  data-testid="calculate-emi-button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/calculator?amount=${propertyPrice}`)}
                >
                  Calculate EMI
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
